import { cleanPreviewText } from "../../lib/format";
import { sourceExcerpt } from "../../lib/storage";
import type { AuthoritativeConcept } from "../../lib/types";

/**
 * A RENDERED PAGE MAY NEVER SHOW SYNTAX — AND IT MAY NEVER SHOW LESS OF THE DOCUMENT THAN
 * THE DOCUMENT ACTUALLY HAS.
 *
 * /settings presents the source document AS A DOCUMENT: a real sheet, set in the document's
 * own serif. Two failures used to break that claim.
 *
 *   1. SYNTAX ON THE PAPER. The moment the sheet prints `# Replaced source` the object stops
 *      being a page and becomes a text editor with a serif font. Markdown is AUTHORING SYNTAX:
 *      a `#` is an instruction to a renderer, not a character the author wanted a reader to
 *      see. `sourceExcerpt` (lib/storage) handles HTML and never handled Markdown, which is a
 *      first-class supported upload — so an uploaded .md put its hashes straight onto paper.
 *
 *   2. ONE SENTENCE ON A WHOLE PAGE. The page rendered `concept.textExcerpt`, and that value
 *      is produced by `cleanPreviewText`, which HARD-CAPS AT 190 CHARACTERS. So a 40-page
 *      concept note rendered as a title and a single clipped sentence on an otherwise empty
 *      sheet — a document preview that is mostly empty page does not read as a document, it
 *      reads as an unfilled placeholder. The 190-char cap is correct for the value STORED in
 *      `textExcerpt` (that field is a preview blurb consumed by /new and by run.ts, and its
 *      shape is frozen), and it is wrong for the PAGE. So the page derives its own body,
 *      uncapped, from `concept.text` — the real captured text — as REAL PARAGRAPHS.
 *
 * HONESTY IS UNTOUCHED. Nothing here invents, completes or paraphrases text. It DROPS syntax
 * characters and shows the prose the author actually wrote, in the order they wrote it. The
 * heading is not deleted from the source; it is promoted to the page's TITLE, where a heading
 * belongs. When no text was captured there is nothing to derive from, and nothing is invented:
 * the caller renders the honest notice instead.
 */

/** ATX (`# Title`) and setext (`Title\n=====`) headings. */
const ATX_HEADING = /^ {0,3}#{1,6}\s+/;
const SETEXT_UNDERLINE = /^ {0,3}(=+|-{2,})\s*$/;
const FENCE = /^\s*(```|~~~)/;
const THEMATIC_BREAK = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/;
const TABLE_ROW = /^\s*\|/;
const FRONT_MATTER = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/;
const HTMLISH = /<[a-z!/][^>]*>/i;

/**
 * How many paragraphs of the real document the sheet renders. A page shows a page's worth of
 * the document and then continues on the next one; it does not attempt to be the document.
 * The visible height is clamped in CSS with a measured fade and an expander, so this is only
 * the ceiling on how much real prose is ever put in the DOM.
 */
const PAGE_BLOCKS = 8;

/** Inline marks, removed so the paper carries words and not markers. */
function stripInlineSyntax(line: string) {
  return line
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/^ {0,3}>\s?/, "")
    .replace(/^ {0,3}[-*+]\s+/, "")
    .replace(/^ {0,3}\d+[.)]\s+/, "")
    .replace(ATX_HEADING, "")
    .trim();
}

function collapse(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

/** The five entities `sourceExcerpt` decodes, decoded here WITHOUT its 190-character cap. */
function decodeEntities(value: string) {
  return value
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

/**
 * The document's own prose, AS PARAGRAPHS, with every heading, fence, rule and table row
 * removed. Fenced code is skipped wholesale: a code block is not the document's argument, and
 * it is certainly not a serif paragraph. A blank line is a paragraph break — which is exactly
 * what it means in the source — so the sheet reproduces the author's own paragraphing rather
 * than mashing a document into one endless run-on line.
 */
function markdownParagraphs(text: string) {
  const lines = text.replace(FRONT_MATTER, "").split(/\r?\n/);
  const paragraphs: string[] = [];
  let current: string[] = [];
  let inFence = false;

  function flush() {
    const joined = collapse(current.join(" "));
    if (joined) paragraphs.push(joined);
    current = [];
  }

  for (const line of lines) {
    if (FENCE.test(line)) {
      inFence = !inFence;
      flush();
      continue;
    }
    if (inFence) continue;

    const trimmed = line.trim();
    if (!trimmed) {
      flush();
      continue;
    }
    /* A heading, a rule and a table row are structure, not prose — and each of them ENDS the
       paragraph that preceded it. */
    if (ATX_HEADING.test(line) || THEMATIC_BREAK.test(line) || TABLE_ROW.test(line)) {
      flush();
      continue;
    }
    /* A setext underline retroactively makes the line before it a heading, so that line is not
       prose either. */
    if (SETEXT_UNDERLINE.test(line)) {
      current.pop();
      continue;
    }

    const cleaned = stripInlineSyntax(line);
    if (cleaned) current.push(cleaned);
  }
  flush();

  return paragraphs;
}

/**
 * The same job for HTML. `sourceExcerpt` flattens every tag to a single space, so an HTML
 * document arrived on the paper as one unbroken wall — its paragraph structure, which is
 * genuinely part of the document, thrown away. Block-level tags become paragraph breaks; the
 * <h1> is dropped because it is promoted to the page's title.
 */
function htmlParagraphs(text: string) {
  return decodeEntities(
    text
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<script[\s\S]*?<\/script>/gi, " ")
      .replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, "\n")
      .replace(/<(?:br|hr)\s*\/?>/gi, "\n")
      .replace(/<\/(?:p|div|section|article|li|ul|ol|h[1-6]|blockquote|tr|table)>/gi, "\n")
      .replace(/<(?:p|div|section|article|li|h[1-6]|blockquote)[^>]*>/gi, "\n")
      .replace(/<[^>]+>/g, " ")
  )
    .split(/\n+/)
    .map(collapse)
    .filter((paragraph) => paragraph.length > 1);
}

/** The prose excerpt STORED with a captured source. Never syntax, never invented text.
    Its 190-character cap is `cleanPreviewText`'s and is deliberate: this value is a preview
    blurb read by /new and by run.ts, and its shape is frozen. The PAGE does not use it. */
export function documentExcerpt(text: string) {
  if (!text.trim()) return "";
  if (HTMLISH.test(text)) return sourceExcerpt(text);
  const prose = markdownParagraphs(text).join(" ");
  /* A document with no prose at all (a heading-only file) gets an honest empty body rather
     than a body reconstructed out of its headings. */
  return cleanPreviewText(prose);
}

/**
 * The title a Markdown or plain-text document gives itself. `sourceTitleFromText` reads
 * <title>/<h1>/og:title and therefore only ever sees HTML; a .md file fell through to the
 * FILENAME, which is why an uploaded note was titled "replaced source" while its own H1 was
 * being printed, hash and all, in the body.
 */
export function documentTitleFromText(text: string) {
  const lines = text.replace(FRONT_MATTER, "").split(/\r?\n/);

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    if (!line.trim()) continue;
    if (ATX_HEADING.test(line)) {
      const title = stripInlineSyntax(line);
      if (title.length >= 4 && title.length <= 90) return title;
      return "";
    }
    if (SETEXT_UNDERLINE.test(lines[index + 1] || "")) {
      const title = stripInlineSyntax(line);
      if (title.length >= 4 && title.length <= 90) return title;
    }
    return "";
  }
  return "";
}

/**
 * WHAT THE PAGE RENDERS: the real captured text, as real paragraphs, uncapped.
 *
 * Derived from `concept.text` on every read, so a source stored by an older build — whose
 * excerpt still carries its Markdown heading, or its 190-character "..." — is repaired rather
 * than printed as syntax on the one object that has to look like a document.
 *
 * The fallback is the stored `textExcerpt`, which is all a source captured by an older build
 * (or one whose text exceeded the storage limit) actually has. It is real text, so it is shown;
 * it is simply short. NOTHING is synthesized to fill the page: when there is no text at all,
 * this returns an empty array and the caller renders the honest notice.
 */
export function documentPageBlocks(concept: AuthoritativeConcept): string[] {
  const text = concept.text || "";
  if (text.trim()) {
    const blocks = HTMLISH.test(text) ? htmlParagraphs(text) : markdownParagraphs(text);
    if (blocks.length) return blocks.slice(0, PAGE_BLOCKS);
  }

  const stored = collapse(concept.textExcerpt || "");
  if (!stored) return [];
  const cleaned = HTMLISH.test(stored) ? collapse(decodeEntities(stored.replace(/<[^>]+>/g, " "))) : stripInlineSyntax(stored);
  return cleaned ? [cleaned] : [];
}
